<template>
  <router-view />
  <Footer v-if="tabShow"></Footer>

  <!-- 新消息提示(后端 WS 推送触发) -->
  <van-dialog v-model:show="show" :title="$t('message.newMessage')" show-cancel-button>
    <div style="padding: 2.66667vw; font-size: 2.66667vw">{{ text }}</div>
  </van-dialog>

  <!-- 充值到账提示 -->
  <van-dialog
    v-model:show="rechargeShow"
    :title="$t('recharge.arrivedTitle')"
    :confirmButtonText="$t('common.confirm')"
  >
    <div class="balance-notify recharge">
      <div class="amount">+ ₡{{ rechargeAmount }}</div>
      <div class="hint">{{ $t('recharge.arrivedHint') }}</div>
      <div class="balance">{{ $t('recharge.newBalance') }}: ₡{{ currentBalance }}</div>
    </div>
  </van-dialog>
</template>

<script>
import Footer from '@/components/Footer';
import { mapGetters } from 'vuex';
import { UserService } from '@/services';
import { storage } from '@/utils';
import { authToken, authId } from '@/constants';

// 余额轮询间隔(秒)—— 10 秒对到账体验和电量消耗折中
const POLL_INTERVAL = 10;

export default {
  name: 'App',
  components: { Footer },
  data() {
    return {
      tabShow: this.$route.meta.tabShow,
      // 新消息弹窗
      show: false,
      text: '',
      // 充值到账弹窗
      rechargeShow: false,
      rechargeAmount: 0,
      currentBalance: 0,
      // 余额轮询相关
      lastAvailableAmount: null,
      lastWaitWithDrawAmount: null,
      pollTimer: null,
      // WebSocket
      ws: null,
    };
  },
  methods: {
    // 判断当前是否登录:优先 Vuex,回退到 localStorage(处理 F5 刷新)
    hasAuth() {
      const token = (this.userInfo && this.userInfo.token) || storage.getItem(authToken);
      const id = (this.userInfo && this.userInfo.id) || storage.getItem(authId);
      return !!(token && id);
    },
    getAuthId() {
      return (this.userInfo && this.userInfo.id) || storage.getItem(authId);
    },
    getAuthToken() {
      return (this.userInfo && this.userInfo.token) || storage.getItem(authToken);
    },

    // 拉一次余额,和上次对比;可用余额增加则触发"充值到账"提示
    async pollBalance() {
      if (!this.hasAuth()) return;
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      try {
        const { code, result } = await UserService.getUserBalanceApi();
        if (code !== 0 || !result) return;

        const available = Number(result.availableAmount) || 0;
        const waitWithDraw = Number(result.waitWithDrawAmount) || 0;

        // eslint-disable-next-line no-console
        console.debug('[balance-poll]', {
          available,
          waitWithDraw,
          lastAvail: this.lastAvailableAmount,
          lastWait: this.lastWaitWithDrawAmount,
        });

        // 首次拉:只记基准,不弹
        if (this.lastAvailableAmount === null) {
          this.lastAvailableAmount = available;
          this.lastWaitWithDrawAmount = waitWithDraw;
          return;
        }

        const availDelta = available - this.lastAvailableAmount;
        const waitDelta = waitWithDraw - this.lastWaitWithDrawAmount;

        // 可用余额增加,且没有伴随的提现减少 → 判定为充值到账
        // (避免提现被拒退回时误报为充值)
        if (availDelta > 0 && waitDelta >= 0) {
          this.currentBalance = available;
          this.rechargeAmount = availDelta;
          this.rechargeShow = true;
          // eslint-disable-next-line no-console
          console.info('[balance-poll] 充值到账 +' + availDelta);
        }

        this.lastAvailableAmount = available;
        this.lastWaitWithDrawAmount = waitWithDraw;
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('[balance-poll] failed:', e && e.message);
      }
    },

    startBalancePolling() {
      this.stopBalancePolling();
      if (!this.hasAuth()) return;
      // eslint-disable-next-line no-console
      console.info('[balance-poll] started (interval:', POLL_INTERVAL, 's)');
      this.pollBalance();
      this.pollTimer = setInterval(() => this.pollBalance(), POLL_INTERVAL * 1000);
      // 页签重新可见时立即拉一次(用户回来的第一时间反馈)
      document.addEventListener('visibilitychange', this.onVisibilityChange);
    },

    stopBalancePolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
      this.lastAvailableAmount = null;
      this.lastWaitWithDrawAmount = null;
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
    },

    onVisibilityChange() {
      if (document.visibilityState === 'visible' && this.hasAuth()) {
        this.pollBalance();
      }
    },

    // 初始化 WebSocket(有 token 就连)
    initWebSocket() {
      const userId = this.getAuthId();
      const token = this.getAuthToken();
      if (!userId || !token) return;
      const url = process.env.VUE_APP_SOCKET_URL;
      if (!url) return;

      const self = this;
      try {
        this.ws = new WebSocket(`ws://${url}/api/v1/frontWs?userId=${userId}&token=${token}`);
        this.ws.onmessage = function (res) {
          if (res.data && res.data.startsWith('{')) {
            const data = JSON.parse(res.data);
            self.show = true;
            self.text = self.$t('message.newMessageText', { title: data.title });
          }
        };
        this.ws.onerror = function () {
          // WS 掉线不影响余额轮询,不打扰用户
        };
      } catch (e) {
        // 忽略
      }
    },
  },

  mounted() {
    // 无论是新登录还是刷新页面,只要 localStorage 里有 token 就启动
    if (this.hasAuth()) {
      this.initWebSocket();
      this.startBalancePolling();
    }
  },

  unmounted() {
    this.stopBalancePolling();
    if (this.ws) {
      try { this.ws.close(); } catch (e) { /* ignore */ }
      this.ws = null;
    }
  },

  computed: {
    ...mapGetters(['userInfo']),
  },

  watch: {
    $route(route) {
      this.tabShow = route.meta.tabShow;
    },
    '$route.name': function () {
      const body = document.body;
      body.style.position = '';
      body.style.top = '';
      const top = body.style.top;
      document.body.scrollTop = document.documentElement.scrollTop = -parseInt(top);
    },
    // 用户登录/切换/登出:重启 WS + 轮询
    'userInfo.id': function (newId) {
      if (newId) {
        this.initWebSocket();
        this.startBalancePolling();
      } else if (!storage.getItem(authToken)) {
        // 真登出了(localStorage 也清了)才停,否则可能是切换过程
        this.stopBalancePolling();
        if (this.ws) {
          try { this.ws.close(); } catch (e) { /* ignore */ }
          this.ws = null;
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.balance-notify {
  padding: 30px 20px;
  text-align: center;
  .amount {
    font-size: 48px;
    font-weight: 700;
    color: #ee0a24;
    margin-bottom: 12px;
  }
  .hint {
    font-size: 28px;
    color: #333;
    margin-bottom: 8px;
  }
  .balance {
    font-size: 24px;
    color: #666;
  }
}
</style>
